import { useState, useEffect } from "react";
import Modal from "@mui/material/Modal";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftBadge from "components/SoftBadge";

import { getAirportById } from "../services/graphqlService";

function toExternalUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw}`;
}

function DetailRow({ label, value, href }) {
  if (value === undefined || value === null || value === "") return null;
  const display = Array.isArray(value)
    ? value
        .map((v) => (typeof v === "object" ? JSON.stringify(v) : v))
        .join(", ")
    : typeof value === "object"
    ? JSON.stringify(value, null, 2)
    : String(value);
  const linkHref = href ? toExternalUrl(href) : "";
  return (
    <Grid item xs={12} sm={6}>
      <SoftBox
        py={1}
        px={1.5}
        sx={{
          borderRadius: 1,
          bgcolor: "grey.50",
        }}
      >
        <SoftTypography
          variant="caption"
          color="text"
          fontWeight="bold"
          display="block"
        >
          {label}
        </SoftTypography>
        {linkHref ? (
          <SoftTypography
            component="a"
            href={linkHref}
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            fontWeight="regular"
            color="info"
            sx={{
              textDecoration: "underline",
              wordBreak: "break-all",
              "&:hover": { opacity: 0.85 },
            }}
          >
            {display}
          </SoftTypography>
        ) : (
          <SoftTypography variant="body2" fontWeight="regular" color="text">
            {display}
          </SoftTypography>
        )}
      </SoftBox>
    </Grid>
  );
}

function SectionTitle({ children }) {
  return (
    <SoftTypography
      variant="button"
      fontWeight="bold"
      color="text"
      sx={{
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        mb: 1,
        display: "block",
      }}
    >
      {children}
    </SoftTypography>
  );
}

function ViewAirportModal({
  open,
  onClose,
  airportId,
  idToken,
  prefetchedRecord,
}) {
  const [airport, setAirport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) {
      setAirport(null);
      setError(null);
      return;
    }
    if (prefetchedRecord) {
      setAirport(prefetchedRecord);
      setLoading(false);
      setError(null);
    }
  }, [open, prefetchedRecord]);

  useEffect(() => {
    if (!open || prefetchedRecord) {
      return;
    }
    if (!airportId || !idToken) {
      setAirport(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    getAirportById(airportId, idToken)
      .then((res) => {
        if (cancelled) return;
        setAirport(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || "Failed to load airport");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, airportId, idToken, prefetchedRecord]);

  const handleClose = () => {
    setAirport(null);
    setError(null);
    onClose();
  };

  const a = airport || {};

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "& .MuiBackdrop-root": { bgcolor: "rgba(0,0,0,0.4)" },
      }}
    >
      <Card
        sx={{
          width: "90%",
          maxWidth: 720,
          maxHeight: "90vh",
          overflow: "auto",
          boxShadow: 24,
          borderRadius: 2,
        }}
      >
        <SoftBox p={3}>
          {/* Header */}
          <SoftBox
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={2}
          >
            <SoftBox>
              <SoftTypography variant="h4" fontWeight="bold" color="dark">
                {a.name || "—"}
              </SoftTypography>
              {(a.airportUse || a.airportOwnership || a.category) && (
                <SoftBox mt={0.5} display="flex" gap={0.75} flexWrap="wrap">
                  {a.airportUse && (
                    <SoftBadge
                      variant="contained"
                      badgeContent={`Use: ${a.airportUse}`}
                      color="info"
                      size="xs"
                      container
                    />
                  )}
                  {a.airportOwnership && (
                    <SoftBadge
                      variant="contained"
                      badgeContent={`Ownership: ${a.airportOwnership}`}
                      color="success"
                      size="xs"
                      container
                    />
                  )}
                  {!a.airportUse && !a.airportOwnership && a.category && (
                    <SoftBadge
                      variant="contained"
                      badgeContent={a.category}
                      color="info"
                      size="xs"
                      container
                    />
                  )}
                </SoftBox>
              )}
            </SoftBox>
            <SoftButton
              variant="text"
              color="secondary"
              iconOnly
              onClick={handleClose}
            >
              <Icon>close</Icon>
            </SoftButton>
          </SoftBox>

          <Divider sx={{ mb: 2 }} />

          {loading && (
            <SoftBox display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </SoftBox>
          )}

          {error && (
            <SoftBox mt={1} mb={2}>
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            </SoftBox>
          )}

          {!loading && !error && airport && (
            <SoftBox>
              {/* Contact */}
              <SectionTitle>Contact</SectionTitle>
              <Grid container spacing={1.5} sx={{ mb: 3 }}>
                <DetailRow label="Email" value={a.email} />
                <DetailRow label="Phone" value={a.phone} />
                <DetailRow label="Website" value={a.url} href={a.url} />
              </Grid>

              {/* Location */}
              <SectionTitle>Location</SectionTitle>
              <Grid container spacing={1.5} sx={{ mb: 3 }}>
                <DetailRow
                  label="Address"
                  value={
                    Array.isArray(a.address)
                      ? a.address[0]?.address
                      : a.address && typeof a.address === "object"
                        ? a.address.address
                        : a.address
                  }
                />
                <DetailRow
                  label="City"
                  value={
                    a.city ||
                    (Array.isArray(a.address)
                      ? a.address[0]?.city
                      : a.address?.city)
                  }
                />
                <DetailRow
                  label="State"
                  value={
                    a.state ||
                    (Array.isArray(a.address)
                      ? a.address[0]?.state
                      : a.address?.state)
                  }
                />
              </Grid>

              {/* Airport classification */}
              {(a.airportUse || a.airportOwnership) && (
                <SoftBox sx={{ mb: 3 }}>
                  <SectionTitle>Airport Classification</SectionTitle>
                  <Grid container spacing={1.5}>
                    <DetailRow label="Airport Use" value={a.airportUse} />
                    <DetailRow
                      label="Airport Ownership"
                      value={a.airportOwnership}
                    />
                  </Grid>
                </SoftBox>
              )}

              {/* Manager */}
              {(a.managerName || a.managerPhone) && (
                <SoftBox sx={{ mb: 3 }}>
                  <SectionTitle>Manager</SectionTitle>
                  <Grid container spacing={1.5}>
                    <DetailRow label="Name" value={a.managerName} />
                    <DetailRow label="Phone" value={a.managerPhone} />
                  </Grid>
                </SoftBox>
              )}
            </SoftBox>
          )}

          {!loading && !error && !airport && open && airportId && (
            <SoftBox py={5} textAlign="center">
              <SoftTypography variant="body2" color="text">
                No airport data returned.
              </SoftTypography>
            </SoftBox>
          )}
        </SoftBox>
      </Card>
    </Modal>
  );
}

export default ViewAirportModal;
